import { Injectable, inject } from '@angular/core';
import { combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BigNumber } from 'bignumber.js';

import { CommonsService } from 'commons/commons.service';
import {
  ApiHeldFunds,
  AuthService,
  ChilledWallet,
  CombinedLedger,
  Currency,
  CurrentEarnRate,
  CustomerLedger,
  EarnService,
  InterestEarned,
  InterestSummary,
  KycLevel,
  Ledger,
  LedgerService,
  LedgerType,
  MarketService,
  ZERO,
  defined,
  zeroPadNumberForDisplay,
} from 'core';
import { Preferences } from 'entities/preferences';
import { DisplayedLedger, SharedMessageService } from 'commons/common-share.service';
import { MessageService } from 'services/message/message.service';
import { ChilledVaultService, Vault } from 'services/chilled-vault.service';
import { DepositService } from './deposit/deposit.service';
import { FundDetailsComponent } from './fund-details/fund-details.component';
import { EarnTransferComponent } from './earn-transfer/earn-transfer.component';
import { EarnBonusTiersComponent } from './earn-bonus-tiers/earn-bonus-tiers.component';

export type PortfolioLedgerSection = 'overview' | 'trade' | 'earn';

@Injectable({
  providedIn: 'root',
})
export class PortfolioService {
  private readonly authService = inject(AuthService);
  private readonly commonsService = inject(CommonsService);
  private readonly messageService = inject(MessageService);
  private readonly marketService = inject(MarketService);
  private readonly depositService = inject(DepositService);
  private readonly modalService = inject(BsModalService);
  private readonly sharedService = inject(SharedMessageService);
  private readonly ledgerService = inject(LedgerService);
  private readonly earnService = inject(EarnService);
  private readonly chilledVaultService = inject(ChilledVaultService);

  private readonly _transactionProcessed = new Subject<void>();
  readonly $transactionProcessed: Observable<void> = this._transactionProcessed.asObservable();

  readonly ZERO = ZERO;
  readonly zeroPadNumberForDisplay = zeroPadNumberForDisplay;
  readonly $chilledVault = this.chilledVaultService.getVault().pipe(shareReplay({ bufferSize: 1, refCount: true }));
  readonly ledgersChanged$ = new Subject<void>();

  activeSection: PortfolioLedgerSection = 'overview';
  searchText = '';
  tradeLedgers = new Array<Ledger>();
  interestEarningLedgers = new Array<Ledger>();
  combinedLedgers = new Array<CombinedLedger>();
  displayedLedgers = new Array<DisplayedLedger>();

  currentRates: Array<CurrentEarnRate> | null = null;
  displayedRates = new Array<CurrentEarnRate>();
  lifetimeBonusInterest?: InterestSummary;
  bonusInterestEarned?: Array<InterestEarned>;
  maxRate: string | null = null;
  hideZeroBalances = false;
  dataLoading = true;

  usdCurrency: Currency;
  zoomCurrency: Currency;
  fundDetails: Array<ApiHeldFunds>;
  isKycApproved = false;

  private initialized = false;
  private ledgersLoaded = false;
  private earnAgreementAccepted: boolean;
  private earnAgreementUrl: string;
  private preferences: Preferences;
  private currencies: Array<Currency>;

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    forkJoin([this.marketService.currencies, this.getPersonalizedSetting()]).subscribe({
      next: ([currencies, prefs]) => {
        if (prefs !== null && prefs !== undefined) {
          this.preferences = prefs;
          this.hideZeroBalances = !this.preferences.showZeroBalances;
        }

        this.currencies = currencies.filter((c) => c.publicFg || this.authService.seePrivateIntruments).sort(currencyComparator);
        this.usdCurrency = currencies.find((c) => c.id === Currency.USD)!;
        this.zoomCurrency = currencies.find((c) => c.id === Currency.ZOOM)!;
        this.refreshLedgers();
      },
      error: (_error) => {
        this.dataLoading = false;
      },
    });

    this.isKycApproved = this.authService.isUserKyc;
    this.getHeldFundsDetails();
  }

  setSection(section: PortfolioLedgerSection): void {
    this.activeSection = section;
    this.setupDisplayedLedgers();
  }

  setupDisplayedLedgers(hideZeroBalancesStatus?: boolean): void {
    if (hideZeroBalancesStatus !== undefined) {
      this.setPersonalizedSetting();
    }

    if (!this.currencies) {
      return;
    }

    if (this.activeSection === 'earn' && this.currentRates == null) {
      if (this.ledgersLoaded) {
        this.loadEarnRates();
      }
      return;
    }

    this.displayedLedgers = new Array<CustomerLedger>();
    const displayedRates = new Array<CurrentEarnRate>();
    this.displayedRates = displayedRates;
    this.getBaseLedgers().forEach((ledger) => {
      if (
        this.matchesSearchFilter(ledger.currency)
        && (!this.hideZeroBalances || !ledger.balance.eq(ZERO) || !ledger.reservedBalance.eq(ZERO))
      ) {
        this.displayedLedgers.push(ledger);
      }
    });

    if (this.currentRates) {
      this.currentRates.forEach((rate) => {
        if (this.matchesSearchFilter(rate.currency) && (!this.hideZeroBalances || rate.hasBalance)) {
          displayedRates.push(rate);
        }
      });
    }

    if (!this.hideZeroBalances && this.currencies) {
      this.currencies.forEach((currency) => {
        if (this.matchesSearchFilter(currency) && this.tradeLedgers.filter((ledger) => ledger.currency.id === currency.id).length === 0) {
          this.displayedLedgers.push(Ledger.buildZeroLedger(LedgerType.TRADING, currency));
        }
      });
    }
    this.sharedService.nextMessage(this.displayedLedgers);
  }

  showBonusInterestTiers(rate: CurrentEarnRate): void {
    this.modalService.show(EarnBonusTiersComponent, {
      ignoreBackdropClick: true,
      class: 'modal-lg',
      initialState: {
        rate,
        zoomCurrency: this.zoomCurrency,
        fullKyc: KycLevel.FULL === this.authService.getLoggedInUserDetails()?.kycLevel,
      },
    });
  }

  invest(rate: CurrentEarnRate): void {
    this.earnTransfer(rate, true);
  }

  redeem(rate: CurrentEarnRate): void {
    this.earnTransfer(rate, false);
  }

  withdrawRestriction(): void {
    if (this.fundDetails && this.fundDetails.length > 0) {
      const initialState = { fundDetails: this.fundDetails };
      this.modalService.show(FundDetailsComponent, {
        ignoreBackdropClick: true,
        class: 'modal-xlg',
        initialState,
      });
    }
  }

  refreshLedgers(includeEarnRates = false): void {
    if (!this.currencies) {
      return;
    }

    this.tradeLedgers = [];
    this.interestEarningLedgers = [];
    this.combinedLedgers = [];
    this.displayedLedgers = [];
    this.dataLoading = true;

    combineLatest([
      this.ledgerService.getLedgers([LedgerType.TRADING, LedgerType.EARNING, LedgerType.STAKING, LedgerType.COLLATERAL]),
      this.$chilledVault,
    ]).subscribe({
      next: ([ledgers, chilledVault]) => {
        this.processLedgers(ledgers, chilledVault);
        this.dataLoading = false;
        if (includeEarnRates || this.currentRates !== null || this.activeSection === 'earn') {
          this.loadEarnRates();
        }
      },
      error: (error) => {
        this.dataLoading = false;
        this.messageService.showErrorMessage(error);
      },
    });
  }

  get hasBonusInterest(): boolean {
    return this.bonusInterestEarned !== undefined && this.bonusInterestEarned.length > 0;
  }

  overallUsd(section = this.activeSection): BigNumber {
    return this.getBaseLedgers(section)
      .map((wallet) => wallet.balanceUsd)
      .reduce((total, balanceUsd) => total.plus(balanceUsd), ZERO);
  }

  overallUsdAvailable(section = this.activeSection): BigNumber {
    return this.getBaseLedgers(section)
      .map((wallet) => wallet.availableUsdBalance)
      .reduce((total, balanceUsd) => total.plus(balanceUsd), ZERO);
  }

  matchesSearchFilter(currency: Currency): boolean {
    return !this.searchText || currency.id.toLowerCase().indexOf(this.searchText.toLowerCase()) >= 0;
  }

  private getPersonalizedSetting(): Observable<Preferences | null> {
    return this.authService.loggedIn ? this.commonsService.getPersonalizedSettingsApi() : of(null);
  }

  private setPersonalizedSetting(): void {
    if (!this.preferences) {
      return;
    }

    this.preferences.showZeroBalances = !this.hideZeroBalances;
    this.commonsService.setPersonalizedSettingsApi(this.preferences).subscribe({
      next: (success) => (this.preferences = success),
      error: (error) => this.messageService.showErrorMessage(error),
    });
  }

  private getHeldFundsDetails(): void {
    this.depositService.getHoldFundsData().subscribe({
      next: (fundsData) => {
        if (defined(fundsData)) {
          this.fundDetails = fundsData;
        }
      },
      error: (error) => this.messageService.showErrorMessage(error),
    });
  }

  private earnTransfer(rate: CurrentEarnRate, toEarn: boolean): void {
    this.modalService.show(EarnTransferComponent, {
      ignoreBackdropClick: true,
      class: 'modal-sm',
      initialState: {
        tradeLedger: this.tradeLedgers.find((ledger) => ledger.currency.id === rate.currency.id),
        earnLedger: this.interestEarningLedgers.find(
          (ledger) => ledger.currency.id === rate.currency.id && ledger.ledgerType === rate.ledgerType,
        ),
        ledgerType: rate.ledgerType,
        toEarn,
        rate,
        agreementAccepted: this.earnAgreementAccepted,
        agreementUrl: this.earnAgreementUrl,
        callback: () => this.refreshLedgers(true),
      },
    });
  }

  private getBaseLedgers(section = this.activeSection): Array<CustomerLedger> {
    switch (section) {
      case 'trade':
        return this.tradeLedgers;
      case 'earn':
        return this.interestEarningLedgers;
      case 'overview':
        return this.combinedLedgers;
      default:
        return [];
    }
  }

  private processLedgers(ledgers: Array<Ledger>, chilledVault: Vault | undefined): void {
    this.tradeLedgers = ledgers ? ledgers.filter((ledger) => ledger.ledgerType === LedgerType.TRADING).sort(ledgerComparator) : [];
    this.interestEarningLedgers = ledgers
      ? ledgers
          .filter((ledger) => ledger.ledgerType === LedgerType.EARNING || ledger.ledgerType === LedgerType.STAKING)
          .sort(ledgerComparator)
      : [];
    this.combinedLedgers = this.combine(ledgers, chilledVault);
    this.ledgersLoaded = true;
    this.setupDisplayedLedgers();
    this.ledgersChanged$.next();
  }

  private loadEarnRates(): void {
    if (!this.authService.seeEarn) {
      return;
    }

    this.dataLoading = true;
    this.earnService.getCurrentRates(this.tradeLedgers.concat(this.interestEarningLedgers)).subscribe({
      next: (rateInfo) => {
        this.processRates(rateInfo.rates);
        this.lifetimeBonusInterest = rateInfo.lifetimeBonusInterest;
        this.bonusInterestEarned = rateInfo.bonusInterestEarned;
        this.earnAgreementAccepted = rateInfo.acceptedAgreement;
        this.earnAgreementUrl = rateInfo.masterLoanAgreementUrl;
        this.dataLoading = false;
      },
      error: (error) => {
        this.dataLoading = false;
        this.messageService.showErrorMessage(error);
      },
    });
  }

  private processRates(rates: CurrentEarnRate[]): void {
    const displayedRates = new Array<CurrentEarnRate>();
    this.displayedRates = displayedRates;
    if (rates && rates.length > 0) {
      this.currentRates = rates;
      const maxRate = rates
        .filter((rate) => rate.allowedToInvest)
        .map((rate) => rate.apy)
        .reduce((prev, curr) => (prev > curr ? prev : curr), 0);
      this.maxRate = zeroPadNumberForDisplay(maxRate, 2);

      this.currentRates.forEach((rate) => {
        if (this.matchesSearchFilter(rate.currency) && (!this.hideZeroBalances || rate.hasBalance)) {
          displayedRates.push(rate);
        }
      });
    }
  }

  private combine(allLedgers: Ledger[], chilledVault: Vault | undefined): CombinedLedger[] {
    const ledgersByCcy = new Map<string, Array<Ledger>>();
    allLedgers.forEach((ledger) => {
      if (!ledgersByCcy.has(ledger.currency.id)) {
        ledgersByCcy.set(ledger.currency.id, []);
      }
      ledgersByCcy.get(ledger.currency.id)?.push(ledger);
    });

    const chilledWalletsByCcy = new Map<string, ChilledWallet>();
    chilledVault?.assets.forEach((asset) => chilledWalletsByCcy.set(asset.currency.id, asset));

    const combinedLedgers = new Array<CombinedLedger>();
    ledgersByCcy.forEach((ledgers, currency) => {
      combinedLedgers.push(new CombinedLedger(ledgers[0].currency, ledgers, chilledWalletsByCcy.get(currency)));
    });

    chilledWalletsByCcy.forEach((chilledWallet, currency) => {
      if (!ledgersByCcy.has(currency)) {
        combinedLedgers.push(new CombinedLedger(chilledWallet.currency, [], chilledWallet));
      }
    });

    return combinedLedgers.sort(ledgerComparator);
  }

  transactionProcessed() {
    this._transactionProcessed.next();
  }
}

const currencyComparator = (c1: Currency, c2: Currency) => {
  if (c1.id === Currency.USD) {
    return -1;
  } else if (c2.id === Currency.USD) {
    return 1;
  } else if (c1.id > c2.id) {
    return 1;
  } else if (c1.id < c2.id) {
    return -1;
  } else {
    return 0;
  }
};

const ledgerComparator = (l1: CustomerLedger, l2: CustomerLedger) => {
  if (l1.currency.id === Currency.USD) {
    return -1;
  } else if (l2.currency.id === Currency.USD) {
    return 1;
  } else if (l1.currency.id > l2.currency.id) {
    return 1;
  } else if (l1.currency.id < l2.currency.id) {
    return -1;
  } else {
    return 0;
  }
};
